import { useState, useEffect } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

import { collectionApis } from '../../../apis/collectionApis';
import AlertMessage from '../../../components/elements/alert-message/AlertMessage';
import Loading from '../../../components/elements/loading/Loading';
import Title from '../../../components/elements/title/Title';
import AddCollectionButton from '../../../components/page-sections/instance-collection/AddCollectionButton';
import InstanceCollectionSection from '../../../components/page-sections/instance-collection/InstanceCollectionSection';
import Breadcrumbs from '../../../components/sections/breadcrumbs/Breadcrumbs';
import { useSolrInstance } from '../../../contexts/SolrContext';
import { handleErrors } from '../../../helpers';
import useToast from '../../../hooks/useToast';
import { isSolrCloud, sentenceCase } from '../../../utils/utils';

const SolrCollectionsPage = () => {
  const navigate = useNavigate();
  const { projectId, instanceId } = useParams();
  const { instance, loading } = useSolrInstance();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const [collections, setCollections] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState(false);
  const [processing, setProcessing] = useState(false);

  const solrCloud = isSolrCloud(instance?.type);

  const fetchCollections = async () => {
    setProcessing(true);
    try {
      if (solrCloud) {
        const { data: collectionRes } = await collectionApis.getClusterStatus(
          projectId,
          instanceId
        );
        const collectionList = collectionRes.cluster.collections;
        const formatData = Object.keys(collectionList).map((key) => ({
          name: key,
          ...collectionList[key],
        }));
        const collectionData = formatData.map((collection) => ({
          name: collection.name,
          replicationFactor: collection.replicationFactor,
          shards: Object.keys(collection.shards).length,
        }));
        setCollections(collectionData);
      } else {
        const { data: collectionRes } = await collectionApis.getCollections(
          projectId,
          instanceId
        );
        const collectionList = Object.values(collectionRes.status);
        setCollections(collectionList);
      }
    } catch (error) {
      handleErrors(navigate, error, showToast);
      setError(true);
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    const onPageLoad = async () => {
      setPageLoading(true);
      await fetchCollections();
      setPageLoading(false);
    };

    onPageLoad();
  }, []);

  if (loading || pageLoading) return <Loading />;

  if (error)
    return (
      <AlertMessage
        severity="error"
        message={sentenceCase(
          t('fetch', {
            ns: 'errors',
            content: t('collection', { ns: 'instance' }),
          })
        )}
        action={fetchCollections}
      />
    );

  return (
    <Container>
      <Breadcrumbs instanceName={instance?.name} />
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title text={t('collections', { ns: 'titles' })} />
        <AddCollectionButton
          collections={collections}
          instanceNodes={instance.instances.length}
          instanceType={instance.type}
          onRefresh={fetchCollections}
        />
      </Box>
      <InstanceCollectionSection
        collections={collections}
        instance={instance}
        isProcessing={processing}
        onRefresh={fetchCollections}
      />
    </Container>
  );
};

export default SolrCollectionsPage;
